// The mutable state of a game: tiles with their stocks and interiors, routes, orders and coins.
// Only commands (through patches) change the layout; only the simulation step changes stocks.
import type { GameData, ItemId, MachineDef, OrderDef, TileDef, VehicleDef } from './data';
import { footprint, keyOf, placeSocket, type Cell, type Rotation, type Side, type Socket } from './geometry';

/** Stock of one port: items waiting at a tile's door, up to a shared capacity. */
export interface Store {
  /** Total items the store holds at most; Infinity for the colony warehouse. */
  readonly capacity: number;
  readonly items: Map<ItemId, number>;
}

export interface MachineState {
  readonly id: string;
  readonly def: MachineDef;
  /** Top-left cell of the footprint. */
  readonly origin: Cell;
  readonly rotation: Rotation;
}

export interface Interior {
  readonly size: Cell;
  readonly machines: Map<string, MachineState>;
  /** Belt cells by `keyOf(cell)`, each with the side its items leave through. */
  readonly belts: Map<string, Side>;
}

export interface TileState {
  readonly def: TileDef;
  /** One store per port, by port id. */
  readonly stores: Map<string, Store>;
  readonly interior?: Interior;
}

export interface PortRef {
  readonly tile: string;
  readonly port: string;
}

export interface RouteState {
  readonly id: string;
  readonly from: PortRef;
  readonly to: PortRef;
  readonly vehicle: VehicleDef;
  /** Length of the road in tiles. */
  readonly length: number;
}

export interface OrderState {
  readonly def: OrderDef;
  /** Items delivered to the colony warehouse since the order started. */
  delivered: number;
  /** Tick at which the order was fulfilled. */
  doneAt?: number;
}

export interface World {
  readonly data: GameData;
  /** Simulated ticks since the game started. */
  tick: number;
  coins: number;
  /** Counter for new machine and route ids. */
  nextId: number;
  /** Bumped whenever the layout changes, so the flow network is rebuilt. */
  layoutVersion: number;
  readonly tiles: Map<string, TileState>;
  readonly routes: Map<string, RouteState>;
  readonly orders: OrderState[];
}

export function newStore(capacity: number): Store {
  return { capacity, items: new Map() };
}

export function storeTotal(items: ReadonlyMap<ItemId, number>): number {
  let total = 0;
  for (const n of items.values()) total += n;
  return total;
}

/** A fresh game of the prototype world: empty interiors, no routes, the starting orders. */
export function createWorld(data: GameData): World {
  const tiles = new Map<string, TileState>();
  for (const def of data.world.tiles.values()) {
    const capacity = def.kind === 'warehouse' ? Infinity : data.world.portCapacity;
    const stores = new Map(def.ports.map((port) => [port.id, newStore(capacity)]));
    tiles.set(def.id, {
      def,
      stores,
      ...(def.interior ? { interior: { size: def.interior, machines: new Map(), belts: new Map() } } : {}),
    });
  }
  return {
    data,
    tick: 0,
    coins: data.world.coins,
    nextId: 1,
    layoutVersion: 0,
    tiles,
    routes: new Map(),
    orders: data.world.orders.map((def) => ({ def, delivered: 0 })),
  };
}

export function machineCells(machine: MachineState): Cell[] {
  return footprint(machine.origin, machine.def.size, machine.rotation);
}

export function machineInput(machine: MachineState): Socket {
  return placeSocket(machine.def.in, machine.origin, machine.def.size, machine.rotation);
}

export function machineOutput(machine: MachineState): Socket {
  return placeSocket(machine.def.out, machine.origin, machine.def.size, machine.rotation);
}

/** Which machine covers each interior cell, by `keyOf(cell)`. */
export function occupancy(interior: Interior): Map<string, MachineState> {
  const cells = new Map<string, MachineState>();
  for (const machine of interior.machines.values()) for (const c of machineCells(machine)) cells.set(keyOf(c), machine);
  return cells;
}

/** Road length between two tiles on the island grid; H3 replaces it with the drawn path. */
export function roadLength(a: Cell, b: Cell): number {
  return Math.max(1, Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]));
}

/** Items per minute a vehicle moves along a road of `length` tiles, going and coming back. */
export function routeThroughput(vehicle: VehicleDef, length: number): number {
  return vehicle.capacity / roundTripMinutes(vehicle, length);
}

export function roundTripMinutes(vehicle: VehicleDef, length: number): number {
  return (2 * length) / vehicle.speed + vehicle.handlingSeconds / 60;
}
